import React, { useCallback, useContext, useEffect, useState } from "react";
import { Container, Table, Spinner, Alert } from "react-bootstrap";
import { DestinazioneContext } from "../context/DestinazioneContext";
import { ThemeContext } from "../context/ThemeContext";
import orderService from "../services/orderService";
import { formatNumber } from "../utils/numberFormat";
import "./WeeklyPlanningTable.css";

const DAY_KEYS = [
  "lunedi",
  "martedi",
  "mercoledi",
  "giovedi",
  "venerdi",
  "sabato",
  "domenica",
];

const DAY_HEADERS = ["lun", "mar", "mer", "giov", "ven", "sab", "dom"];

const normalizeUnitaMisura = (orderData) =>
  orderData.unitaMisura ||
  orderData.unita_m ||
  orderData.unita ||
  (orderData.dati_articolo && orderData.dati_articolo.unita_m);

const formatQtyCell = (value, unitaMisura) => {
  const n = Number(value);
  if (Number.isNaN(n)) return "—";
  const u = (unitaMisura || "").toLowerCase();
  if (u === "kg") return formatNumber(n, 3) || "0";
  return String(Math.floor(n));
};

function WeeklyPlanningTable() {
  const { destinazione } = useContext(DestinazioneContext);
  const { darkMode } = useContext(ThemeContext);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadWeekly = useCallback(async () => {
    if (!destinazione) return;
    setLoading(true);
    setError(null);
    try {
      const data = await orderService.getOrdineStandard("lunedi");
      const mapped = (data || []).map((order) => ({
        codice: order.codice,
        descriz: order.descriz || "",
        unitaMisura: normalizeUnitaMisura(order),
        lunedi: order.lunedi ?? 0,
        martedi: order.martedi ?? 0,
        mercoledi: order.mercoledi ?? 0,
        giovedi: order.giovedi ?? 0,
        venerdi: order.venerdi ?? 0,
        sabato: order.sabato ?? 0,
        domenica: order.domenica ?? 0,
      }));
      mapped.sort((a, b) => String(a.codice).localeCompare(String(b.codice), "it"));
      setRows(mapped);
    } catch (err) {
      setError(err?.message || "Errore nel caricamento della tabella settimanale.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [destinazione]);

  useEffect(() => {
    loadWeekly();
  }, [loadWeekly]);

  return (
    <Container
      fluid
      className={`py-3 weekly-planning-page ${darkMode ? "bg-dark text-light" : ""}`}
      data-bs-theme={darkMode ? "dark" : "light"}
    >
      <h3 className="mb-2 h4">Tabella Settimanale</h3>
      <p className="text-body-secondary small mb-3 mb-md-4">
        Quantità pianificate per articolo e giorno (sola lettura).
      </p>

      {!destinazione && (
        <Alert variant="info">Seleziona una destinazione per visualizzare la tabella.</Alert>
      )}

      {error && <Alert variant="danger">{error}</Alert>}

      {destinazione && loading && (
        <div className="text-center py-5">
          <Spinner animation="border" role="status" />
        </div>
      )}

      {destinazione && !loading && !error && (
        <div className="weekly-planning-wrap">
          <div className="table-responsive">
            <Table
              bordered
              size="sm"
              variant={darkMode ? "dark" : "light"}
              className="align-middle weekly-planning-readonly mb-0"
            >
              <thead className={darkMode ? "table-dark" : "table-light"}>
                <tr>
                  <th scope="col">Articolo</th>
                  {DAY_HEADERS.map((h) => (
                    <th key={h} scope="col" className="text-center text-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center text-body-secondary py-4 small">
                      Nessun articolo in planning.
                    </td>
                  </tr>
                ) : (
                  rows.flatMap((r, idx) => {
                    const blockClass = idx % 2 === 0 ? "weekly-pt-block-even" : "";
                    return [
                      <tr
                        key={`${r.codice}-q`}
                        className={`weekly-pt-row-qty ${blockClass}`}
                      >
                        <td className="weekly-pt-articolo">{r.codice}</td>
                        {DAY_KEYS.map((dk) => (
                          <td key={dk} className="weekly-pt-qty">
                            {formatQtyCell(r[dk], r.unitaMisura)}
                          </td>
                        ))}
                      </tr>,
                      <tr
                        key={`${r.codice}-d`}
                        className={`weekly-pt-row-desc ${blockClass}`}
                      >
                        <td colSpan={8} className="weekly-pt-desc">
                          {r.descriz || "—"}
                        </td>
                      </tr>,
                    ];
                  })
                )}
              </tbody>
            </Table>
          </div>
        </div>
      )}
    </Container>
  );
}

export default WeeklyPlanningTable;
